import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ photos }) {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 160]);
  const featured =
    photos.find((p) => p.id === "13609aabd862431bbf6bb8f957c8ede6") ||
    photos.find((p) => p.type !== "video" && p.image_url);
  const img = featured ? `${BACKEND_URL}${featured.image_url}` : null;
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute -inset-y-24 inset-x-0 bg-[#241F16]">
        {img && (
          <img
            src={img}
            alt="The Power of Midlife retreat at Buckhorn Hollow Farms"
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-[#241F16]/50" />
      </motion.div>
      <div className="relative z-10 text-center px-6 pt-20 pb-24">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="text-[11px] tracking-[0.35em] uppercase text-[#F5F1E6]/85 mb-8"
        >
          Prime Collective presents
        </motion.p>
        <h1
          data-testid="hero-title"
          className="font-serif font-normal text-5xl sm:text-6xl lg:text-8xl leading-[1.02] text-[#F5F1E6]"
        >
          <MaskedLine delay={0.25}>Come find</MaskedLine>
          <MaskedLine delay={0.42}>
            <span className="italic">yourself here.</span>
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-8 font-serif italic text-xl sm:text-2xl text-[#F5F1E6]/90"
        >
          The Power of Midlife · October 2–4, 2026 · Buckhorn Hollow Farms
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-5 max-w-xl mx-auto text-sm sm:text-base leading-relaxed text-[#F5F1E6]/80 font-light"
        >
          The photos from our weekend are here. Grab your favorites — beautiful enough to print,
          frame, and share.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-8"
        >
          <button
            onClick={() => go("gallery")}
            data-testid="hero-browse-btn"
            className="border border-[#F5F1E6]/70 text-[#F5F1E6] px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-[#F5F1E6] hover:text-[#3C3527] transition-colors duration-300"
          >
            Browse the archive
          </button>
          <button
            onClick={() => go("upload")}
            data-testid="hero-upload-btn"
            className="text-[11px] tracking-[0.25em] uppercase text-[#F5F1E6] underline underline-offset-8 decoration-[#F5F1E6]/50 hover:text-[#C4562F] hover:decoration-[#C4562F] transition-colors duration-300"
          >
            Add photos
          </button>
        </motion.div>
      </div>
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#F5F1E6]/70"
      >
        <ArrowDown size={14} /> Scroll
      </motion.div>
    </section>
  );
}
